import { useContext, useEffect, useRef, useState } from "react";
import { useEventStream } from "../context/EventStreamingContext";
import useImageResizer from "../utils/useImageResizer";
import { callFunction } from "../api/callFunction";
import { serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth, db, storage } from "../firebase";
import { ref, deleteObject } from "firebase/storage";
import { GlobalContext } from "../context/GlobalContext";
import { fetchUsersWithIDs } from "./utils";
import { showToast } from "../utils/showToast";
import VendorAPI from "../api/VendorApi";
import EventAPI from "../api/EventApi";
import { getEventDataObject } from "./utils/formConstants";
import {
  VARIANT_ID,
  resolveVariantCopy,
  withVariantPhoto,
} from "./utils/listingVariants";
import { validateEventFields } from "./utils/formUtils";
import PlacesAPI from "../api/PlacesAPI";
import {
  toDateSafe,
  toMillisSafe,
  NOTIFY_DATE_CHANGE_AGE_MS,
} from "./formSectionHelpers";
import { useEventActions } from "./useEventActions";

export const useEventForm = ({ sourceSuggestionId = null, sourceRequestId = null, photoGateBlocked = false }) => {
  const { eventData, loading, setEventUsersProfile } = useEventStream();
  const { globalState } = useContext(GlobalContext);
  const { setImageForResize } = useImageResizer(600, 800);
  const [usersDb, setUsersDB] = useState([]);
  // Which photo the library modal is picking for: "event" for the event's own
  // strip, "variant" for the second listing's single hero image, null when it is
  // closed. One modal, two destinations, so the confirm has to know which.
  const [libraryTarget, setLibraryTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [generatingImage, setGeneratingImage] = useState(false);
  // Separate from generatingImage: generating for one must not put the other
  // button into its loading state.
  const [generatingVariantImage, setGeneratingVariantImage] = useState(false);
  const [generatingVariantCopy, setGeneratingVariantCopy] = useState(false);
  const [regeneratingVideo, setRegeneratingVideo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [pricingUnlocked, setPricingUnlocked] = useState(false);
  const [pricingDetailsOpen, setPricingDetailsOpen] = useState(false);
  const [showHostPinPicker, setShowHostPinPicker] = useState(false);
  const [showCancelWizard, setShowCancelWizard] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // Date-change notify prompt: `dateNotify` holds { eventId, newDate } once a
  // save moved the date of a >1-week-old event with attendees; `notifyDraft` is
  // the editable message body.
  const [dateNotify, setDateNotify] = useState(null);
  const [notifyDraft, setNotifyDraft] = useState("");
  const [sendingNotify, setSendingNotify] = useState(false);

  // Places autocomplete state
  const [venueSuggestions, setVenueSuggestions] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showVenueSuggestions, setShowVenueSuggestions] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const venueDebounceRef = useRef(null);
  const addressDebounceRef = useRef(null);
  // placeId of the selected venue — used for Google Places photo fetching
  const [venuePlaceId, setVenuePlaceId] = useState(null);
  // Only show the map preview once a place has been picked (or loaded from a saved event)
  const [placePicked, setPlacePicked] = useState(
    !!(eventData?.hostname && eventData?.address)
  );

  const [event, setEvent] = useState(
    getEventDataObject({ ...eventData, vendorID: eventData?.vendorID || globalState.vendorID || "" })
  );

  const navigate = useNavigate();

  useEffect(() => {
    setEvent(getEventDataObject({ ...eventData, vendorID: eventData?.vendorID || globalState.vendorID || "" }));
    setPlacePicked(!!(eventData?.hostname && eventData?.address));
  }, [eventData]);

  useEffect(() => {
    const loadUsersWithNames = async () => {
      try {
        const existingUserIds = new Set(Object.keys(event.eventUsers || {}));
        const missingUserIds = [
          ...new Set([
            ...event.movers,
            ...event.sitters,
            ...event.rsvpMovers,
            ...event.rsvpSitters,
            ...event.invitedSitters,
          ]),
        ].filter((id) => !existingUserIds.has(id));

        if (missingUserIds.length === 0) return;

        const allUsers = await fetchUsersWithIDs(db, missingUserIds);
        setUsersDB(allUsers);
        setEventUsersProfile(allUsers);
      } catch (error) {
        console.error("Error fetching users: ", error);
      }
    };

    loadUsersWithNames();
  }, [setEventUsersProfile, event.movers, event.sitters, event.rsvpMovers, event.rsvpSitters, event.invitedSitters, event.eventUsers]);

  useEffect(() => {
    const loadVendors = async () => {
      if (!globalState?.isSuperAdmin) return;
      setLoadingVendors(true);
      try {
        const vendorsData = await VendorAPI.fetchAllVendors();
        const vendorIDs = vendorsData.map((v) => v.vendorID).filter(Boolean);
        const users = await fetchUsersWithIDs(db, vendorIDs);
        const userNameByID = users.reduce((acc, u) => {
          acc[u.userID] = u.name;
          return acc;
        }, {});
        const vendorsWithUserNames = vendorsData.map((v) => ({
          ...v,
          name: userNameByID[v.vendorID] || v.name,
        }));
        setVendors(vendorsWithUserNames);
      } catch (error) {
        console.error("Error fetching vendors: ", error);
        showToast("Failed to load vendors", { type: "error" });
      } finally {
        setLoadingVendors(false);
      }
    };

    loadVendors();
  }, [globalState?.isSuperAdmin]);

  const handleChange = (field, value) => {
    setEvent((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (field, subfield, value) => {
    setEvent((prev) => ({
      ...prev,
      [field]: { ...prev[field], [subfield]: value },
    }));
  };

  const handleCostCommit = async (rawValue) => {
    if (!event.id) return;
    try {
      await EventAPI.patchEvent(event.id, {
        cost: Number(rawValue) || 0,
      });
      showToast("Price updated", { type: "success" });
    } catch (error) {
      console.error("Error auto-saving price:", error);
      showToast("Failed to update price", { type: "error" });
    }
  };

  // ------------------------------------------------------------------
  // Venue Name autocomplete handlers
  // ------------------------------------------------------------------
  const handleVenueChange = (e) => {
    const value = e.target.value;
    handleChange("hostname", value);
    if (!value) setPlacePicked(false);
    clearTimeout(venueDebounceRef.current);
    venueDebounceRef.current = setTimeout(async () => {
      const suggestions = await PlacesAPI.autocomplete(value, { includedPrimaryTypes: ["establishment"] });
      setVenueSuggestions(suggestions);
      setShowVenueSuggestions(suggestions.length > 0);
    }, 300);
  };

  const handleVenueSelect = (suggestion) => {
    const pred = suggestion.placePrediction;
    const venueName = pred?.structuredFormat?.mainText?.text || pred?.text?.text || "";
    const venueAddress = pred?.structuredFormat?.secondaryText?.text || "";
    handleChange("hostname", venueName);
    if (venueAddress) handleChange("address", venueAddress);
    if (pred?.placeId) setVenuePlaceId(pred.placeId);
    setPlacePicked(true);
    setVenueSuggestions([]);
    setShowVenueSuggestions(false);
  };

  // ------------------------------------------------------------------
  // Address autocomplete handlers
  // ------------------------------------------------------------------
  const handleAddressChange = (e) => {
    const value = e.target.value;
    handleChange("address", value);
    if (!value) setPlacePicked(false);
    clearTimeout(addressDebounceRef.current);
    addressDebounceRef.current = setTimeout(async () => {
      const suggestions = await PlacesAPI.autocomplete(value, { includedPrimaryTypes: ["geocode"] });
      setAddressSuggestions(suggestions);
      setShowAddressSuggestions(suggestions.length > 0);
    }, 300);
  };

  const handleAddressSelect = (suggestion) => {
    const pred = suggestion.placePrediction;
    const fullAddress = pred?.text?.text || pred?.structuredFormat?.mainText?.text || "";
    handleChange("address", fullAddress);
    setPlacePicked(true);
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
  };

  // ------------------------------------------------------------------
  // Group spot (events.hostPin) — the "where the group is sitting" pin
  // ------------------------------------------------------------------
  const handleHostPinConfirm = (lat, lng, label) => {
    const trimmedLabel = (label || "").slice(0, 60);
    const uid = auth.currentUser?.uid || "";
    handleChange("hostPin", {
      lat,
      lng,
      label: trimmedLabel,
      setBy: uid,
      setAt: serverTimestamp(),
    });
    // Legacy mirror: the iOS "meeting point" feature predates hostPin and
    // already-shipped clients only read `meetingPoint` (lat/lng -> latitude/
    // longitude). Mirror it so admin-set spots show on every client, not just
    // the find-the-group gate (which reads the canonical hostPin). Reconcile
    // away once those clients age out.
    //
    // Why the two fields aren't merged (it's NOT just an old name): per
    // firestore.rules, `meetingPoint` is ATTENDEE-writable (any guest can set
    // it), while `hostPin` is host/admin-ONLY. The find-the-group pin must be
    // host-authored and not hijackable, so hostPin is the canonical/trusted
    // field. Don't collapse onto meetingPoint — retire meetingPoint, keep hostPin.
    handleChange("meetingPoint", {
      latitude: lat,
      longitude: lng,
      label: trimmedLabel,
      setBy: uid,
      setAt: serverTimestamp(),
    });
    setShowHostPinPicker(false);
  };

  const handleHostPinClear = () => {
    handleChange("hostPin", null);
    handleChange("meetingPoint", null);
  };

  // ------------------------------------------------------------------

  const handlePhotoChange = async (e) => {
    const files = e.target.files;
    const newPhotos = await Promise.all(
      Array.from(files).map((photo) => setImageForResize(photo))
    );
    handleChange("photos", [...(event.photos || []), ...newPhotos]);
  };

  const handleGenerateImage = async (keywords) => {
    const { errors } = validateEventFields(event, false, !!eventData?.id);
    if (errors.length > 0) {
      showToast(errors.join(", "), { type: "error" });
      return;
    }

    setGeneratingImage(true);
    try {
      const result = await callFunction("generateImageForEvent", {
        name: event.name,
        date: event.date,
        description: event.description,
        hostname: event.hostname,
        address: event.address,
        keywords: typeof keywords === "string" && keywords ? keywords : undefined,
      });

      if (result && result.success && result.imageData) {
        const imageDataUrl = `data:image/png;base64,${result.imageData}`;
        handleChange("photos", [...(event.photos || []), imageDataUrl]);
        showToast("Image generated and added to photos!", { type: "success" });
      } else {
        showToast("Image generation completed, but no image data received.", { type: "warning" });
      }
    } catch (error) {
      console.error("Error generating image:", error);
      showToast("Failed to generate image. Please try again.", { type: "error" });
    } finally {
      setGeneratingImage(false);
    }
  };

  // ------------------------------------------------------------------
  // Second listing copy
  // ------------------------------------------------------------------

  /**
   * Ask the model to write this listing's name and description.
   *
   * `instruction` is whatever the author typed ("shorter, pitch it at people
   * new to the city"). The event's own copy always goes along with it, because that is
   * what the second listing has to differ from and where the frozen facts (the
   * schedule, the venue) are read out of.
   *
   * The current draft goes too when there is one, so a second press means
   * "shorter than what I am looking at" rather than "start again from the
   * event". Getting that backwards is what makes a rewrite button feel broken.
   *
   * Nothing is saved. The copy lands in the form fields for the author to read
   * and edit, exactly as if they had typed it, and reaches Firestore only when
   * they save.
   */
  const handleGenerateVariantCopy = async (instruction) => {
    if (generatingVariantCopy) return null;

    // Only the two fields the copy is written from, rather than the full event
    // validation the image path runs: a venue and an address sharpen the
    // writing but nothing here needs them.
    if (!event.name?.trim() || !event.description?.trim()) {
      showToast("Give the event a name and description first — the second listing is written from them.", {
        type: "error",
      });
      return null;
    }

    const variant = event.listingVariants?.[VARIANT_ID] || {};

    setGeneratingVariantCopy(true);
    try {
      const result = await callFunction(
        "generateListingVariantCopy",
        {
          baseName: event.name,
          baseDescription: event.description,
          draftName: variant.name || "",
          draftDescription: variant.description || "",
          instruction: typeof instruction === "string" ? instruction.trim() : "",
          hostname: event.hostname || "",
          address: event.address || "",
        },
        {
          // Must outlive the callable's own 240s deadline, or the SDK abandons a
          // request the server is still working on and bills for it anyway. The
          // SDK default is 70s. Deadlines only work here if each one is longer
          // than the one it wraps: client > callable > one Gemini attempt.
          timeout: 250000,
        }
      );

      const { success, name, description, warnings } = result || {};
      if (!success || (!name && !description)) {
        showToast("Nothing came back. Please try again.", { type: "warning" });
        return null;
      }

      setEvent((prev) => ({
        ...prev,
        listingVariants: {
          ...(prev.listingVariants || {}),
          [VARIANT_ID]: {
            ...((prev.listingVariants || {})[VARIANT_ID] || {}),
            // A blank half is left alone rather than cleared: an empty field
            // inherits from the event, and silently wiping the author's wording
            // is worse than leaving half of it in place.
            ...(name ? { name } : {}),
            ...(description ? { description } : {}),
          },
        },
      }));

      showToast("Second listing written. Read it before you save.", { type: "success" });
      return { warnings: Array.isArray(warnings) ? warnings : [] };
    } catch (error) {
      console.error("Error writing second listing copy:", error);
      showToast(error?.message || "Could not write the second listing.", { type: "error" });
      return null;
    } finally {
      setGeneratingVariantCopy(false);
    }
  };

  // ------------------------------------------------------------------
  // Second listing photo
  //
  // The same three ways in the event's own photos have (generate, library,
  // upload), pointed at one field instead of an album. They live here rather
  // than in the section component because the work is the same Firebase work,
  // and because what they produce is the same as what the event's photos hold:
  // a File or a base64 data URL, not a URL. uploadListingVariantPhotos turns
  // those into storage URLs at save time, on both write paths.
  // ------------------------------------------------------------------

  // ONE hero image, never an album, so every route in replaces the photo.
  // Writing "" is how it goes back to inheriting the event's own photo.
  const setVariantPhoto = (photo) =>
    setEvent((prev) => ({
      ...prev,
      listingVariants: withVariantPhoto(prev.listingVariants, photo),
    }));

  const handleVariantPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    // Clear the input so picking the same file again still fires a change.
    e.target.value = "";
    if (!file) return;
    setVariantPhoto(await setImageForResize(file));
  };

  const handleGenerateVariantImage = async (keywords) => {
    const { errors } = validateEventFields(event, false, !!eventData?.id);
    if (errors.length > 0) {
      showToast(errors.join(", "), { type: "error" });
      return;
    }

    // Generate from the copy this listing will actually show: its own overrides
    // where it has them, the event's wording where it does not. The whole point
    // of a variant is a different pitch, so generating from the base copy would
    // make an image for the listing this one exists to differ from. Venue fields
    // stay the event's, because a variant never changes where the night happens.
    const { name, description } = resolveVariantCopy(
      event.listingVariants?.[VARIANT_ID],
      event
    );

    setGeneratingVariantImage(true);
    try {
      const result = await callFunction("generateImageForEvent", {
        name,
        date: event.date,
        description,
        hostname: event.hostname,
        address: event.address,
        keywords: typeof keywords === "string" && keywords ? keywords : undefined,
      });

      if (result && result.success && result.imageData) {
        setVariantPhoto(`data:image/png;base64,${result.imageData}`);
        showToast("Image generated for the second listing!", { type: "success" });
      } else {
        showToast("Image generation completed, but no image data received.", { type: "warning" });
      }
    } catch (error) {
      console.error("Error generating second listing image:", error);
      showToast("Failed to generate image. Please try again.", { type: "error" });
    } finally {
      setGeneratingVariantImage(false);
    }
  };

  // Ask for a fresh clip. The callable only marks the event, so there is nothing
  // to await here beyond the request landing: the doc stream walks the status
  // row from queued to ready on its own.
  const handleRegenerateVideo = async () => {
    if (regeneratingVideo || !eventData?.id) return;

    setRegeneratingVideo(true);
    try {
      await callFunction("regenerateEventVideo", { eventID: eventData.id });
      showToast("Making a new video. It takes a minute or two.", { type: "success" });
    } catch (error) {
      console.error("Error regenerating event video:", error);
      showToast(error?.message || "Could not start the video.", { type: "error" });
    } finally {
      setRegeneratingVideo(false);
    }
  };

  const handleLibraryConfirm = (selectedUrls) => {
    if (libraryTarget === "variant") {
      // The library allows multi-select, but the second listing has one hero
      // image. The first pick wins and the rest are ignored, rather than the
      // selection landing in the event's photo strip.
      const [first] = selectedUrls || [];
      if (first) setVariantPhoto(first);
    } else {
      handleChange("photos", [...new Set([...(event.photos || []), ...selectedUrls])]);
    }
    setLibraryTarget(null);
  };

  const removePhoto = (index) => {
    const updatedPhotos = event.photos.filter((_, idx) => idx !== index);
    handleChange("photos", updatedPhotos);
  };

  const handleDeleteFromStorage = async (index) => {
    const photo = event.photos[index];
    if (typeof photo === "string" && photo.startsWith("https://firebasestorage.googleapis.com/")) {
      try {
        const photoRef = ref(storage, photo);
        await deleteObject(photoRef);
        showToast("Photo deleted from storage!", { type: "success" });
      } catch (error) {
        console.error("Error deleting photo from storage:", error);
        showToast("Failed to delete photo from storage.", { type: "error" });
        return;
      }
    }
    removePhoto(index);
  };

  const onSessionTimeChange = (type) => {
    if (type == null || event.sessionTime === type.id) return;
    handleChange("sessionTime", type.id);
  };

  const onSitterSizeChange = (v) => {
    if (event.size?.sitters === v) return;
    handleNestedChange("size", "sitters", v);
  };

  const onMoverSizeChange = (v) => {
    if (event.size?.movers === v) return;
    handleNestedChange("size", "movers", v);
  };

  // Inline date-change notice: the moment the (edit-mode) date diverges from the
  // saved value on an established event that already has attendees, surface a
  // prompt to notify them. `eventLikelyEstablished` uses createdAt when present
  // and falls back to "established" when it's missing (legacy/imported events
  // lack the field, and the web SDK can't read Firestore's server createTime).
  const savedEventDate = toDateSafe(eventData?.date);
  const currentEventDate = toDateSafe(event.date);
  const eventCreatedMs = toMillisSafe(eventData?.createdAt);
  const eventLikelyEstablished =
    eventCreatedMs == null ||
    Date.now() - eventCreatedMs > NOTIFY_DATE_CHANGE_AGE_MS;
  const dateChangeAttendeeCount = new Set([
    ...(event.rsvpSitters || []),
    ...(event.sitters || []),
    ...(event.movers || []),
    ...(event.rsvpMovers || []),
  ]).size;
  const showDateChangeNotice =
    !!eventData?.id &&
    !!savedEventDate &&
    !!currentEventDate &&
    Math.abs(savedEventDate.getTime() - currentEventDate.getTime()) > 60 * 1000 &&
    eventLikelyEstablished &&
    dateChangeAttendeeCount > 0;

  const {
    handleDeleteEvent,
    handleUncancelEvent,
    handleSubmit,
    handleSaveAndNotifyDateChange,
  } = useEventActions({
    event,
    eventData,
    usersDb,
    globalState,
    sourceSuggestionId,
    sourceRequestId,
    photoGateBlocked,
    notifyDraft,
    navigate,
    setSubmitting,
    setProgressMessage,
    setDateNotify,
    setSendingNotify,
  });

  return {
    loading,
    event,
    globalState,
    eventData,
    handleChange,
    handleNestedChange,
    handleCostCommit,
    handlePhotoChange,
    removePhoto,
    handleDeleteFromStorage,
    handleGenerateImage,
    generatingImage,
    handleRegenerateVideo,
    regeneratingVideo,
    handleLibraryConfirm,
    showLibrary: libraryTarget !== null,
    libraryTarget,
    openLibrary: setLibraryTarget,
    closeLibrary: () => setLibraryTarget(null),
    handleVariantPhotoChange,
    handleGenerateVariantImage,
    generatingVariantImage,
    handleGenerateVariantCopy,
    generatingVariantCopy,
    venuePlaceId,
    pricingUnlocked,
    setPricingUnlocked,
    pricingDetailsOpen,
    setPricingDetailsOpen,
    savedEventDate,
    currentEventDate,
    showDateChangeNotice,
    placePicked,
    showHostPinPicker,
    setShowHostPinPicker,
    showCancelWizard,
    setShowCancelWizard,
    handleHostPinConfirm,
    handleHostPinClear,
    handleVenueChange,
    handleVenueSelect,
    venueSuggestions,
    showVenueSuggestions,
    setShowVenueSuggestions,
    handleAddressChange,
    handleAddressSelect,
    addressSuggestions,
    showAddressSuggestions,
    setShowAddressSuggestions,
    showSettings,
    setShowSettings,
    vendors,
    loadingVendors,
    onSessionTimeChange,
    onSitterSizeChange,
    onMoverSizeChange,
    submitting,
    progressMessage,
    dateNotify,
    notifyDraft,
    setNotifyDraft,
    setDateNotify,
    sendingNotify,
    handleUncancelEvent,
    handleDeleteEvent,
    handleSaveAndNotifyDateChange,
    handleSubmit,
  };
};
